import { createContext, useContext, useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { v4 as uuidv4 } from 'uuid';

const API = process.env.REACT_APP_BACKEND_URL || "";

const WishlistContext = createContext();

export const useWishlist = () => {
  const context = useContext(WishlistContext);
  if (!context) {
    throw new Error('useWishlist must be used within a WishlistProvider');
  }
  return context;
};

export const WishlistProvider = ({ children }) => {
  const [wishlistItems, setWishlistItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [isDrawerOpen, setIsDrawerOpen] = useState(false);
  
  // Get or create session ID
  const getSessionId = useCallback(() => {
    let sessionId = localStorage.getItem('wishlist_session_id');
    if (!sessionId) {
      sessionId = uuidv4();
      localStorage.setItem('wishlist_session_id', sessionId);
    }
    return sessionId;
  }, []);

  // Fetch wishlist from backend
  const fetchWishlist = useCallback(async () => {
    try {
      const sessionId = getSessionId();
      const response = await axios.get(`${API}/api/wishlist/${sessionId}`);
      setWishlistItems(response.data.items || []);
    } catch (error) {
      // Wishlist fetch failed - start with empty list
    } finally {
      setLoading(false);
    }
  }, [getSessionId]);

  // Load wishlist on mount
  useEffect(() => {
    fetchWishlist();
  }, [fetchWishlist]);

  // Add to wishlist
  const addToWishlist = async (productId) => {
    try {
      const sessionId = getSessionId();
      const response = await axios.post(`${API}/api/wishlist/${sessionId}/add`, {
        product_id: productId
      });
      
      if (response.data.success) {
        await fetchWishlist(); // Refresh to get product details
        return { success: true, action: response.data.action };
      }
      return { success: false };
    } catch (error) {
      return { success: false, error: 'Failed to add to wishlist' };
    }
  };

  // Remove from wishlist
  const removeFromWishlist = async (productId) => {
    try {
      const sessionId = getSessionId();
      await axios.delete(`${API}/api/wishlist/${sessionId}/remove/${productId}`);
      setWishlistItems(prev => prev.filter(item => item.product_id !== productId));
      return { success: true };
    } catch (error) {
      return { success: false, error: 'Failed to remove from wishlist' };
    }
  };

  // Toggle wishlist (add/remove)
  const toggleWishlist = async (productId) => {
    const isInWishlist = wishlistItems.some(item => item.product_id === productId);
    
    if (isInWishlist) {
      return removeFromWishlist(productId);
    } else {
      return addToWishlist(productId);
    }
  };

  // Check if product is in wishlist
  const isInWishlist = (productId) => {
    return wishlistItems.some(item => item.product_id === productId);
  };

  // Open/close drawer
  const openDrawer = () => setIsDrawerOpen(true);
  const closeDrawer = () => setIsDrawerOpen(false);

  return (
    <WishlistContext.Provider 
      value={{
        wishlistItems,
        loading,
        wishlistCount: wishlistItems.length,
        addToWishlist,
        removeFromWishlist,
        toggleWishlist,
        isInWishlist,
        isDrawerOpen,
        openDrawer,
        closeDrawer,
        refreshWishlist: fetchWishlist
      }}
    >
      {children}
    </WishlistContext.Provider>
  );
};

export default WishlistContext;
